/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/top.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Collects the top values for a field. Includes repeated values.
 *
 * @example
 * FROM employees
 * | STATS top_salaries = TOP(salary, 3, "desc"), top_salary = MAX(salary)
 */
const definition: FunctionDefinition = {
  type: 'agg',
  name: 'top',
  signatures: [
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'date',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'double',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'keyword',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'long',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'text',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'date',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'double',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'keyword',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'long',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'text',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'date',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'double',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'keyword',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'long',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'text',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'date',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'double',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'keyword',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'long',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'text',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'date',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'double',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'keyword',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'long',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'text',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'date',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'double',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'integer',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'keyword',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'long',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'limit',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
        {
          name: 'outputField',
          type: 'text',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
